import React, { useMemo, useState, useCallback } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import CodeIcon from '@mui/icons-material/Code';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PRMetrics } from './types';
import { UIPRWithTypedCoverage, UIPRCoverageDelta } from '../../../../../../api/teamMetricsApi';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(4),
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  headerTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: '#F7FAFC',
    },
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  titleText: {
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#1A1A1A',
  },
  subtitle: {
    fontSize: 14,
    color: '#8E9299',
  },
  divider: {
    width: 1,
    height: 32,
    backgroundColor: '#E2E8F0',
  },
  calendarIcon: {
    fontSize: 16,
    color: '#8E9299',
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  summaryRow: {
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'stretch',
  },
  tableContainer: {
    border: '1px solid #E2E8F0',
    borderRadius: 4,
  },
  tableHead: {
    backgroundColor: '#F7FAFC',
  },
  headerCell: {
    fontSize: 10,
    fontWeight: 700,
    color: '#8E9299',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    borderBottom: '1px solid #E2E8F0',
    whiteSpace: 'nowrap',
  },
  colPullRequest: {
    width: '30%',
    minWidth: 200,
  },
  colService: {
    width: '15%',
    minWidth: 100,
  },
  colChanges: {
    width: '10%',
    minWidth: 80,
  },
  colDelta: {
    width: '12%',
    minWidth: 90,
  },
  colAuthor: {
    width: '15%',
    minWidth: 100,
  },
  colDate: {
    width: '10%',
    minWidth: 80,
  },
  prTitleLink: {
    fontSize: 14,
    fontWeight: 700,
    color: '#0070F3',
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 250,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  insightOutliers: {
    fontSize: 11,
    color: '#2B6CB0',
    marginTop: theme.spacing(0.5),
  },
  insightOutlierItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.25),
  },
  insightOutlierLink: {
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  tableRowHighlighted: {
    backgroundColor: '#EBF8FF !important',
    transition: 'background-color 0.3s ease-in-out',
  },
  tableRow: {
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#F7FAFC',
    },
  },
  cell: {
    borderBottom: '1px solid #E2E8F0',
    padding: theme.spacing(2),
  },
  prTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#1A1A1A',
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 250,
  },
  prId: {
    fontSize: 10,
    fontFamily: 'monospace',
    color: '#8E9299',
  },
  serviceCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    overflow: 'hidden',
  },
  serviceIcon: {
    fontSize: 14,
    color: '#4A5568',
  },
  serviceText: {
    fontSize: 12,
    color: '#4A5568',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  changesCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    fontFamily: 'monospace',
    fontSize: 10,
    fontWeight: 700,
  },
  additions: {
    color: '#38A169',
  },
  deletions: {
    color: '#E53E3E',
  },
  deltaPositive: {
    fontFamily: 'monospace',
    fontSize: 14,
    fontWeight: 700,
    color: '#38A169',
  },
  deltaNegative: {
    fontFamily: 'monospace',
    fontSize: 14,
    fontWeight: 700,
    color: '#E53E3E',
  },
  deltaNeutral: {
    fontFamily: 'monospace',
    fontSize: 14,
    fontWeight: 700,
    color: '#4A5568',
  },
  authorCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
    padding: theme.spacing(0.5),
    borderRadius: 4,
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#F7FAFC',
    },
  },
  authorAvatar: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    backgroundColor: '#EBF8FF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 700,
    color: '#2B6CB0',
  },
  authorName: {
    fontSize: 12,
    color: '#0070F3',
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  dateText: {
    fontSize: 12,
    color: '#8E9299',
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: 'center',
    color: '#8E9299',
    fontStyle: 'italic',
  },
  summaryCard: {
    padding: theme.spacing(2, 3),
    border: '1px solid #E2E8F0',
    borderRadius: 4,
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  summaryLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: '#8E9299',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  statValue: {
    fontSize: 24,
    fontWeight: 700,
    color: '#1A1A1A',
  },
  statValuePositive: {
    fontSize: 24,
    fontWeight: 700,
    color: '#38A169',
  },
  statValueNegative: {
    fontSize: 24,
    fontWeight: 700,
    color: '#E53E3E',
  },
  insightCard: {
    padding: theme.spacing(2),
    backgroundColor: '#EBF8FF',
    border: '1px solid #BEE3F8',
    borderRadius: 4,
  },
  insightHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: '#2B6CB0',
  },
  insightIcon: {
    fontSize: 16,
  },
  insightTitle: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  insightText: {
    fontSize: 12,
    color: '#2B6CB0',
    lineHeight: 1.5,
  },
  filterContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  userFilter: {
    minWidth: 300,
  },
}));

interface PRImpactTableProps {
  prs?: PRMetrics[];
  typedPrs?: UIPRWithTypedCoverage[];
  title: string;
  subtitle: string;
  onBack?: () => void;
  dateRangePicker?: React.ReactNode;
  onAuthorClick: (username: string) => void;
  availableUsers?: string[];
  selectedUsers?: string[];
  onSelectedUsersChange?: (users: string[]) => void;
  activityChart?: React.ReactNode;
}

function formatDelta(delta: number | null): string {
  if (delta === null) return 'N/A';
  const formatted = delta.toFixed(2);
  return delta >= 0 ? `+${formatted}%` : `${formatted}%`;
}

function getCombinedDelta(coverageDeltas: UIPRCoverageDelta[]): string {
  const combined = coverageDeltas.find((d) => d.testType === 'go_combined');
  if (combined && combined.delta !== null) {
    return formatDelta(combined.delta);
  }
  return 'N/A';
}

export const PRImpactTable: React.FC<PRImpactTableProps> = ({
  prs,
  typedPrs,
  title,
  subtitle,
  onBack,
  dateRangePicker,
  onAuthorClick,
  availableUsers,
  selectedUsers,
  onSelectedUsersChange,
  activityChart,
}) => {
  const classes = useStyles();
  const [highlightedPrId, setHighlightedPrId] = useState<string | null>(null);

  const handleOutlierClick = useCallback((prId: string) => {
    const rowElement = document.getElementById(`pr-row-${prId}`);
    if (rowElement) {
      rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedPrId(prId);
      setTimeout(() => setHighlightedPrId(null), 2000);
    }
  }, []);

  // Normalize to common format
  type NormalizedPR = {
    id: string;
    title: string;
    author: string;
    weaveUser: string;
    service: string;
    coverageDelta: string | null;
    coverageDeltas?: UIPRCoverageDelta[];
    date: string;
    additions: number;
    deletions: number;
    prUrl: string;
    repoIsExcluded: boolean;
  };

  const normalizedPRs: NormalizedPR[] = useMemo(() => {
    if (typedPrs && typedPrs.length > 0) {
      return typedPrs.map((pr) => ({
        id: pr.id,
        title: pr.title,
        author: pr.author,
        weaveUser: pr.weaveUser,
        service: pr.service,
        coverageDelta: getCombinedDelta(pr.coverageDeltas),
        coverageDeltas: pr.coverageDeltas,
        date: pr.date,
        additions: pr.additions,
        deletions: pr.deletions,
        prUrl: pr.prUrl,
        repoIsExcluded: pr.repoIsExcluded ?? false,
      }));
    }
    return (prs ?? []).map((pr) => ({
      id: pr.id,
      title: pr.title,
      author: pr.author,
      weaveUser: pr.weaveUser,
      service: pr.service,
      coverageDelta: pr.coverageDelta,
      date: pr.date,
      additions: pr.additions,
      deletions: pr.deletions,
      prUrl: pr.prUrl,
      repoIsExcluded: false,
    }));
  }, [prs, typedPrs]);

  const filteredPRs = useMemo(() => {
    return normalizedPRs.filter((pr) => pr);
  }, [normalizedPRs]);

  const avgDelta = useMemo(() => {
    const prsWithDelta = filteredPRs.filter((pr) => pr.coverageDelta !== null && pr.coverageDelta !== 'N/A');
    if (prsWithDelta.length === 0) return '0.00';
    const total = prsWithDelta.reduce((acc, pr) => {
      const value = parseFloat(pr.coverageDelta!.replace('%', '').replace('+', ''));
      return acc + (isNaN(value) ? 0 : value);
    }, 0);
    return (total / prsWithDelta.length).toFixed(2);
  }, [filteredPRs]);

  const coverageStats = useMemo(() => {
    const included = filteredPRs.filter(pr => !pr.repoIsExcluded);
    const withDelta = included
      .map(pr => {
        const combined = pr.coverageDeltas?.find(d => d.testType === 'go_combined');
        const delta = combined?.delta ?? null;
        return { pr, delta };
      })
      .filter((item): item is { pr: NormalizedPR; delta: number } => item.delta !== null);

    if (withDelta.length === 0) {
      return { avgDelta: 0, count: 0, highOutliers: [], lowOutliers: [] };
    }

    const totalDelta = withDelta.reduce((sum, item) => sum + item.delta, 0);
    const avgDeltaValue = totalDelta / withDelta.length;
    const highOutliers = withDelta
      .filter(item => item.delta > 3)
      .sort((a, b) => b.delta - a.delta)
      .slice(0, 2);
    const lowOutliers = withDelta
      .filter(item => item.delta < -3)
      .sort((a, b) => a.delta - b.delta)
      .slice(0, 2);

    return { avgDelta: avgDeltaValue, count: withDelta.length, highOutliers, lowOutliers };
  }, [filteredPRs]);

  const getDeltaClass = (delta: string | null) => {
    if (delta === null) return classes.deltaNeutral;
    if (delta.startsWith('+')) return classes.deltaPositive;
    if (delta.startsWith('-')) return classes.deltaNegative;
    return classes.deltaNeutral;
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Box className={classes.headerTop}>
          <Box className={classes.titleSection}>
            {onBack && (<IconButton className={classes.backButton} onClick={onBack}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box className={classes.titleText}>
              <Typography className={classes.title}>{title}</Typography>
              <Typography className={classes.subtitle}>{subtitle}</Typography>
            </Box>
          </Box>
          {dateRangePicker && <Box>{dateRangePicker}</Box>}
        </Box>

        {/* Filters row */}
        <Box className={classes.filterContainer}>
          {availableUsers && availableUsers.length > 0 && onSelectedUsersChange && (
            <Autocomplete
              multiple
              size="small"
              className={classes.userFilter}
              options={availableUsers}
              value={selectedUsers ?? []}
              onChange={(_, newValue) => onSelectedUsersChange(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={selectedUsers?.length === 0 ? 'Filter by user...' : ''}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option.split('@')[0]}
                    size="small"
                  />
                ))
              }
              getOptionLabel={(option) => option.split('@')[0]}
              limitTags={2}
            />
          )}
        </Box>

        {/* Activity chart (if provided) */}
        {activityChart}

        {/* Summary cards */}
        <Box className={classes.summaryRow}>
          <Paper className={classes.insightCard} elevation={0} style={{ flex: 1 }}>
            <Box className={classes.insightHeader}>
              <InfoOutlinedIcon className={classes.insightIcon} />
              <Typography className={classes.insightTitle}>Insight</Typography>
            </Box>
            {coverageStats.count > 0 ? (
              <>
                {coverageStats.highOutliers.length > 0 && (
                  <Box className={classes.insightOutliers}>
                    <Typography component="span" style={{ fontWeight: 600 }}>Top gains: </Typography>
                    {coverageStats.highOutliers.map((item, idx) => (
                      <span key={item.pr.id}>
                        {idx > 0 && ', '}
                        <span
                          className={classes.insightOutlierLink}
                          onClick={() => handleOutlierClick(item.pr.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && handleOutlierClick(item.pr.id)}
                        >
                          {item.pr.title.substring(0, 30)}{item.pr.title.length > 30 ? '...' : ''} (+{item.delta.toFixed(1)}%)
                        </span>
                      </span>
                    ))}
                  </Box>
                )}
                {coverageStats.lowOutliers.length > 0 && (
                  <Box className={classes.insightOutliers}>
                    <Typography component="span" style={{ fontWeight: 600 }}>Needs review: </Typography>
                    {coverageStats.lowOutliers.map((item, idx) => (
                      <span key={item.pr.id}>
                        {idx > 0 && ', '}
                        <span
                          className={classes.insightOutlierLink}
                          onClick={() => handleOutlierClick(item.pr.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && handleOutlierClick(item.pr.id)}
                        >
                          {item.pr.title.substring(0, 30)}{item.pr.title.length > 30 ? '...' : ''} ({item.delta.toFixed(1)}%)
                        </span>
                      </span>
                    ))}
                  </Box>
                )}
                {coverageStats.highOutliers.length === 0 && coverageStats.lowOutliers.length === 0 && (
                  <Typography className={classes.insightText}>
                    No significant coverage outliers in recent PRs.
                  </Typography>
                )}
              </>
            ) : (
              <Typography className={classes.insightText}>
                {parseFloat(avgDelta) >= 0
                  ? 'Team is trending positive — healthy focus on quality.'
                  : 'Coverage dipped — review PRs with negative deltas.'}
              </Typography>
            )}
          </Paper>
        </Box>
      </Box>

      <Box className={classes.mainContent}>
        <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
          <Table>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={`${classes.headerCell} ${classes.colPullRequest}`}>Pull Request</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.colService}`}>Service</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.colChanges}`} align="right">Changes</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.colDelta}`} align="right">Cov. Delta</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.colAuthor}`} align="center">Author</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.colDate}`} align="right">Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPRs.length > 0 ? (
                filteredPRs.map((pr) => (
                  <TableRow
                    key={pr.id}
                    id={`pr-row-${pr.id}`}
                    className={`${classes.tableRow} ${highlightedPrId === pr.id ? classes.tableRowHighlighted : ''}`}
                  >
                    <TableCell className={classes.cell}>
                      {pr.prUrl ? (
                        <Tooltip title={pr.title} placement="top-start" enterDelay={300}>
                          <Typography
                            component="a"
                            href={pr.prUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={classes.prTitleLink}
                          >
                            {pr.title}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Tooltip title={pr.title} placement="top-start" enterDelay={300}>
                          <Typography className={classes.prTitle}>{pr.title}</Typography>
                        </Tooltip>
                      )}
                      <Typography className={classes.prId}>{pr.id}</Typography>
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <Box className={classes.serviceCell}>
                        <CodeIcon className={classes.serviceIcon} />
                        <Typography className={classes.serviceText}>{pr.service}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell className={classes.cell} align="right">
                      <Box className={classes.changesCell}>
                        <span className={classes.additions}>+{pr.additions}</span>
                        <span className={classes.deletions}>-{pr.deletions}</span>
                      </Box>
                    </TableCell>
                    <TableCell className={classes.cell} align="right">
                      <Typography className={getDeltaClass(pr.coverageDelta)}>
                        {pr.coverageDelta ?? 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.cell} align="center">
                      <Box
                        className={classes.authorCell}
                        onClick={() => pr.weaveUser && onAuthorClick(pr.weaveUser)}
                        style={{ cursor: pr.weaveUser ? 'pointer' : 'default' }}
                      >
                        <Box className={classes.authorAvatar}>
                          {pr.author.charAt(0).toUpperCase()}
                        </Box>
                        <Typography className={classes.authorName}>{pr.author}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell className={classes.cell} align="right">
                      <Typography className={classes.dateText}>{pr.date}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className={classes.emptyState}>
                    No Pull Requests found for the selected timeframe.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default PRImpactTable;
